import React, { useEffect, useState } from 'react';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import { useHistory } from 'react-router-dom';

// getDisplayName - Proper convention for wrapping display name for ease of debugging
// See {@link https://reactjs.org/docs/higher-order-components.html#convention-wrap-the-display-name-for-easy-debugging}
function getDisplayName(WrappedComponent) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}

const INDEX_SEARCH_PARAM_KEY = 'index';

const getIndexSearchParam = history => {
  const params = new URLSearchParams(history.location.search);
  return parseInt(params.get(INDEX_SEARCH_PARAM_KEY), 10);
};

const removeIndexSearchParam = history => {
  const params = new URLSearchParams(history.location.search);
  params.delete(INDEX_SEARCH_PARAM_KEY);
  history.replace({ search: params.toString() });
};

const setIndexSearchParam = (history, nextIndex) => {
  const params = new URLSearchParams(history.location.search);
  params.set(INDEX_SEARCH_PARAM_KEY, nextIndex);
  history.replace({ search: params.toString() });
};

const updateIndexSearchParam = (history, nextIndex) => {
  const params = new URLSearchParams(history.location.search);
  params.set(INDEX_SEARCH_PARAM_KEY, nextIndex);
  history.push({ search: params.toString() });
};

/**
 * withCollectionStepper is an HOC thatr manages stepping through a collection by providing a single child component
 * with the following props:
 *
 * resource - The current resource in the collection that is being displayed
 *
 * index - index of current resource in collection ("One-based numbering" arrayIndex + 1)
 *
 * total - Total number of resources in the collection
 *
 * next - A function to move to the next resource.
 *
 * previous - A function to move to the previous resource.
 *
 * isStart - True if index is at starting resource
 *
 * isEnd - True if index is at last resource
 *
 * @component
 * @param collection - Facet Rest Collection to be stepped through
 * @param WrappedComponent - Wrapped component
 */
function withCollectionStepper(collection, WrappedComponent) {
  return function WithCollectionStepper(props) {
    // Set display name. See getDisplayName above
    withCollectionStepper.displayName = `WithCollectionStepper(${getDisplayName(
      WrappedComponent,
    )})`;

    const history = useHistory();
    const collArray = collection.resources().list();
    const total = collArray.length;
    const indexParam = getIndexSearchParam(history);

    // If index param is NaN or >= the total amount of resources in the provided collection set the param to 0
    let initialIndexParam = Number.isNaN(indexParam) ? 0 : indexParam;
    if (initialIndexParam >= total) {
      initialIndexParam = 0;
    }
    const [index, setIndex] = useState(initialIndexParam);
    const resource = collArray[index];

    useComponentDidMount(() => {
      if (indexParam !== initialIndexParam) {
        setIndexSearchParam(history, initialIndexParam);
      }
    });

    useEffect(() => {
      if (index !== indexParam) {
        updateIndexSearchParam(history, index);
      }
    }, [index]);

    const finished = () => {
      removeIndexSearchParam(history);
    };

    const next = () => {
      if (index < total - 1) {
        setIndex(index + 1);
      }
    };

    const previous = () => {
      if (index > 0) {
        setIndex(index - 1);
      }
    };

    const setResourceAtIndex = (resourceToSet, indexToSet) => {
      collection.resources().replaceAtIndex(indexToSet, resourceToSet);
    };

    return (
      <WrappedComponent
        resource={resource}
        index={index + 1}
        total={total}
        finished={finished}
        next={next}
        previous={previous}
        isStart={index === 0}
        isEnd={index === total - 1}
        setResourceAtIndex={setResourceAtIndex}
        {...props}
      />
    );
  };
}

export default withCollectionStepper;
